import React from 'react'

export interface DrawerState {
  readonly pinned: boolean
  readonly expanded: boolean
}

enum DrawerAction {
  togglePin,
  mouseEnter,
  mouseLeave
}

/**
 * the reducer accepts a state and an action, returns a new state. (See finite state machine)
 *
 * NOTE: a typical convention is to type action as: {type: enum<event type>, payload: Object<event details>}
 * in this case, there is no payload, so the action is just the type.
 */
function drawerReducer(
  { pinned, expanded }: DrawerState,
  action: DrawerAction
): DrawerState {
  switch (action) {
    case DrawerAction.togglePin:
      return {
        expanded: expanded || !pinned,
        pinned: !pinned
      }
    case DrawerAction.mouseEnter:
      return {
        expanded: true,
        pinned
      }
    case DrawerAction.mouseLeave:
      return {
        expanded: pinned,
        pinned
      }
  }
}
/**
 * If the drawer is pinned, stays expanded regardless of mouseEnter / mouseLeave events.
 * If the drawer pinned state is toggled, the expanded state should be forced to be the new pinned state
 * If the drawer is unpinned, mouseEnter triggers expanded state, mouseLeave triggers closed state.
 * @param inititallyPinned
 * @returns [{pinned, expanded}, event handlers {onMouseEnter, onMouseLeave, onTogglePinned}]
 */
export function useDrawer(
  inititallyPinned: boolean
): readonly [
  DrawerState,
  {
    readonly onMouseLeave: () => void
    readonly onMouseEnter: () => void
    readonly onTogglePinned: () => void
  }
] {
  const initalState: DrawerState = {
    expanded: inititallyPinned,
    pinned: inititallyPinned
  }

  const [state, dispatch] = React.useReducer(drawerReducer, initalState)

  // dispatch takes in an action and runs the reducer function to calculate and update the next state.
  // this function returns a function that calls dispatch for the provided action.
  function runDispatch(action: DrawerAction): () => void {
    return (): void => dispatch(action)
  }

  const onMouseLeave = runDispatch(DrawerAction.mouseLeave)
  const onMouseEnter = runDispatch(DrawerAction.mouseEnter)
  const onTogglePinned = runDispatch(DrawerAction.togglePin)
  return [state, { onMouseLeave, onMouseEnter, onTogglePinned }]
}
